import React, { useEffect, useMemo, useRef, useState } from "react";

// Campo di ricerca a caricamento dinamico: input text con dropdown filtrato.
// Sostituisce <select> nativi dove si vuole scrivere parzialmente e vedere le
// corrispondenze.
//
// Props:
//   options: array di oggetti
//   value: valore corrente (id)
//   getLabel(option) -> string, testo mostrato + criterio di filtro
//   getValue(option) -> primitive, identificativo confrontato con value
//   onSelect(option) -> void, chiamato alla selezione
//   placeholder: string
//   disabled: bool
//   maxItems: numero massimo di risultati mostrati (default 50)
const SearchableSelect = ({
    options = [],
    value,
    getLabel,
    getValue,
    onSelect,
    placeholder = "Cerca...",
    disabled = false,
    maxItems = 50,
}) => {
    const [query, setQuery] = useState("");
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const wrapperRef = useRef(null);

    // Trova la label dell'opzione selezionata per mostrarla quando l'input non
    // è in edit mode.
    const selectedLabel = useMemo(() => {
        if (value === undefined || value === null || value === "" || value === "null") return "";
        const found = options.find((o) => String(getValue(o)) === String(value));
        return found ? getLabel(found) : "";
    }, [value, options, getLabel, getValue]);

    // Se l'input non è aperto, mostra la label selezionata; se aperto, mostra
    // ciò che l'utente sta scrivendo.
    const displayValue = open ? query : selectedLabel;

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        const list = q
            ? options.filter((o) => getLabel(o).toLowerCase().includes(q))
            : options;
        return list.slice(0, maxItems);
    }, [options, query, getLabel, maxItems]);

    useEffect(() => {
        const onDocClick = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, []);

    const handleSelect = (opt) => {
        onSelect(opt);
        setQuery("");
        setOpen(false);
        setActiveIndex(-1);
    };

    const handleKeyDown = (e) => {
        if (!open && (e.key === "ArrowDown" || e.key === "Enter")) {
            setOpen(true);
            return;
        }
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIndex((i) => Math.max(i - 1, 0));
        } else if (e.key === "Enter") {
            e.preventDefault();
            if (activeIndex >= 0 && filtered[activeIndex]) handleSelect(filtered[activeIndex]);
        } else if (e.key === "Escape") {
            setOpen(false);
        }
    };

    return (
        <div ref={wrapperRef} style={{ position: "relative" }}>
            <input
                type="text"
                className="form-control validate"
                placeholder={placeholder}
                value={displayValue}
                disabled={disabled}
                onFocus={() => {
                    setOpen(true);
                    setQuery("");
                }}
                onChange={(e) => {
                    setQuery(e.target.value);
                    setOpen(true);
                    setActiveIndex(-1);
                }}
                onKeyDown={handleKeyDown}
                autoComplete="off"
            />
            {open && filtered.length > 0 && (
                <ul
                    style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        zIndex: 1050,
                        maxHeight: "220px",
                        overflowY: "auto",
                        margin: 0,
                        padding: 0,
                        listStyle: "none",
                        background: "white",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                    }}
                >
                    {filtered.map((opt, idx) => {
                        const val = getValue(opt);
                        const isSelected = String(val) === String(value);
                        const isActive = idx === activeIndex;
                        return (
                            <li
                                key={val}
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    handleSelect(opt);
                                }}
                                onMouseEnter={() => setActiveIndex(idx)}
                                style={{
                                    padding: "8px 12px",
                                    cursor: "pointer",
                                    background: isActive
                                        ? "#f5f5f5"
                                        : isSelected
                                        ? "#fff5ef"
                                        : "white",
                                    fontWeight: isSelected ? 600 : 400,
                                }}
                            >
                                {getLabel(opt)}
                            </li>
                        );
                    })}
                </ul>
            )}
            {open && filtered.length === 0 && (
                <div
                    style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        zIndex: 1050,
                        padding: "8px 12px",
                        background: "white",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        color: "#888",
                        fontSize: "0.9em",
                    }}
                >
                    Nessuna corrispondenza
                </div>
            )}
        </div>
    );
};

export default SearchableSelect;
